import React, { Suspense, Component } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stage, Center, useGLTF, Bounds, Environment } from '@react-three/drei';
import { MDBSpinner } from 'mdb-react-ui-kit';
import './product-3d.css';

function Model({ url }) {
  const { scene } = useGLTF(url);
  return <primitive object={scene.clone()} />;
}

// Error boundary to catch WebGL/Three.js crashes
class ViewerErrorBoundary extends Component {
  state = { hasError: false, errorMessage: '' };

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || 'Unknown error' };
  }

  componentDidCatch(error, info) {
    console.error('3D Viewer Error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p3d-viewer-error">
          <i className="fas fa-exclamation-triangle fa-2x text-danger mb-2" />
          <span className="text-danger fw-bold mb-1">Failed to load 3D model</span>
          <small className="text-muted">{this.state.errorMessage}</small>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function ThreeDModelViewer({
  modelUrl,
  interactive = false,
  className = '',
}) {
  if (!modelUrl) {
    return (
      <div className={`p3d-viewer p3d-viewer-placeholder ${className}`}>
        <span className="text-muted">No 3D model available</span>
      </div>
    );
  }

  return (
    <div className={`p3d-viewer ${className}`}>
      <ViewerErrorBoundary>
        <Suspense
          fallback={
            <div className="p3d-viewer-loading">
              <MDBSpinner role="status">
                <span className="visually-hidden">Loading...</span>
              </MDBSpinner>
            </div>
          }
        >
          <Canvas
            camera={{ position: [0, 1, 5], fov: 45 }}
            className="p3d-viewer-canvas"
          >
            {interactive ? (
              <>
                <Environment preset="city" />
                <ambientLight intensity={0.5} />
                <Bounds fit clip margin={1.5}>
                  <Center>
                    <Model url={modelUrl} />
                  </Center>
                </Bounds>
                <OrbitControls
                  makeDefault
                  enableZoom
                  enablePan
                  enableRotate
                  minDistance={0.1}
                  maxDistance={20}
                />
              </>
            ) : (
              <Stage environment="city" intensity={0.5} adjustCamera>
                <Center>
                  <Model url={modelUrl} />
                </Center>
              </Stage>
            )}
          </Canvas>
        </Suspense>
      </ViewerErrorBoundary>
    </div>
  );
}
